/**
 * Wave-3 replay engine — the virtual clock. Advances tVirtual across the
 * replay span while playing; the UI binds buildTimeline().stateAt(tVirtual)
 * to it. Replay is entered ONLY by user gesture (play()) — it starts paused
 * and never autoplays.
 *
 * prefers-reduced-motion is evaluated INSIDE the hook body (never at module
 * level): jsdom/SSR may not define window.matchMedia, and the preference must
 * be re-read per effect run, not frozen at import time.
 */
import { useCallback, useEffect, useState } from 'react';
import type { ReplaySpan } from './replayTimeline';

const NORMAL_TICK_MS = 250;
const REDUCED_MOTION_TICK_MS = 1000;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export interface ReplayClock {
  tVirtual: number;
  playing: boolean;
  speed: number;
  play: () => void;
  pause: () => void;
  seek: (fraction: number) => void;
  done: boolean;
}

export function useReplayClock(span: ReplaySpan, speed: number): ReplayClock {
  const [tVirtual, setTVirtual] = useState<number>(span.start);
  const [playing, setPlaying] = useState<boolean>(false);

  // A new span is a new replay — restart from the beginning, paused.
  useEffect(() => {
    setTVirtual(span.start);
    setPlaying(false);
  }, [span.start, span.end]);

  useEffect(() => {
    if (!playing) return;
    const tickMs = prefersReducedMotion() ? REDUCED_MOTION_TICK_MS : NORMAL_TICK_MS;
    const id = window.setInterval(() => {
      setTVirtual((t) => {
        const next = t + speed * tickMs;
        // Clamp (never ===-compare): the last tick may overshoot span.end, and
        // done must still fire.
        if (next >= span.end) {
          setPlaying(false);
          return span.end;
        }
        return next;
      });
    }, tickMs);
    return () => window.clearInterval(id);
  }, [playing, speed, span.end]);

  const play = useCallback(() => setPlaying(true), []);
  const pause = useCallback(() => setPlaying(false), []);

  const seek = useCallback(
    (fraction: number) => {
      const f = Math.min(1, Math.max(0, fraction));
      setTVirtual(span.start + f * (span.end - span.start));
    },
    [span.start, span.end]
  );

  return {
    tVirtual,
    playing,
    speed,
    play,
    pause,
    seek,
    done: tVirtual >= span.end,
  };
}
